import React, { useState, useEffect } from 'react';
import { Stack, Typography, Box, Button, Alert, CircularProgress, Input, FormControl, FormLabel } from '@mui/joy';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useSession } from 'context/SessionContext';
import { getSubscriptionPaymentIntent, getEmployerPositions } from '../../../../apiconfig/api';

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

if (!STRIPE_PUBLIC_KEY) {
  throw new Error("Missing STRIPE_PUBLIC_KEY in environment variables");
}

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

interface PaymentStepProps {
  data: {
    selectedPlan: string;
    paymentMethod: string;
    promoCode?: string;
  };
  onUpdate: (updates: any) => void;
  onNext: () => void;
  isSubmitting?: boolean;
  onFormReady?: (isReady: boolean) => void;
}

interface PaymentFormProps {
  clientSecret: string;
  paymentIntentId: string;
  onNext: () => void;
  isSubmitting?: boolean;
  onFormReady: (isReady: boolean) => void;
  promoCode: string;
  onPromoCodeChange: (code: string) => void;
  onPromoCodeBlur: () => void;
  hasExistingPositions: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ paymentIntentId, onNext, isSubmitting, onFormReady, promoCode, onPromoCodeChange, onPromoCodeBlur, hasExistingPositions }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);

  // Check if promo code is valid
  const isValidPromoCode = promoCode === 'FIRSTPOSTFREE' && !hasExistingPositions;

  // Track form readiness
  React.useEffect(() => {
    // If valid promo code, form is always ready
    if (isValidPromoCode) {
      onFormReady(true);
      return;
    }

    if (!stripe || !elements) {
      onFormReady(false);
      return;
    }

    const paymentElement = elements.getElement('payment');
    if (!paymentElement) {
      onFormReady(false);
      return;
    }

    const handleChange = (event: any) => {
      // Form is ready when it's complete and has no errors
      const isReady = event.complete && !event.error;
      onFormReady(isReady);
    };

    paymentElement.on('change', handleChange);

    // Cleanup
    return () => {
      paymentElement.off('change', handleChange);
    };
  }, [stripe, elements, onFormReady, isValidPromoCode]);

  // Handle payment submission when triggered from parent
  React.useEffect(() => {
    if (isSubmitting) {
      const handleSubmission = async () => {
        setError(null);

        try {
          // If valid promo code, skip payment and proceed
          if (isValidPromoCode) {
            console.log('Valid promo code applied: FIRSTPOSTFREE');
            onNext(); // Proceed to next step
            return;
          }

          // Otherwise, process payment normally
          if (!stripe || !elements) {
            throw new Error('Payment system not ready');
          }

          const { error: submitError } = await elements.submit();
          if (submitError) {
            throw new Error(submitError.message || 'Payment submission failed');
          }

          const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: window.location.origin,
            },
            redirect: 'if_required',
          });

          if (confirmError) {
            throw new Error(confirmError.message || 'Payment confirmation failed');
          } else if (paymentIntent?.status === 'succeeded') {
            // Payment successful, log the payment intent details
            console.log('Payment succeeded:', {
              paymentIntentId: paymentIntentId,
              confirmedPaymentIntentId: paymentIntent.id,
              amount: paymentIntent.amount,
              status: paymentIntent.status
            });
            
            onNext(); // Proceed to next step
          } else {
            throw new Error(`Payment failed with status: ${paymentIntent?.status}`);
          }
        } catch (err: any) {
          setError(err.message || 'An unexpected error occurred');
        }
      };

      handleSubmission();
    }
  }, [isSubmitting, stripe, elements, paymentIntentId, onNext, isValidPromoCode]);

  return (
    <Stack spacing={4}>
      {/* Plan Description */}
      <Box sx={{ 
        p: 3, 
        backgroundColor: '#F8F9FA', 
        borderRadius: '12px', 
        border: '2px solid #E5E7EB',
        textAlign: 'center'
      }}>
        <Typography level="h3" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
          Employer Unlimited Plan
        </Typography>
        <Typography level="h2" sx={{ fontWeight: 700, color: '#2e2d7e', mb: 2 }}>
          $50
        </Typography>
        <Stack spacing={1} sx={{ textAlign: 'left', maxWidth: '400px', mx: 'auto' }}>
          <Typography level="body-sm" sx={{ color: '#1A1B1D', display: 'flex', alignItems: 'center', gap: 1 }}>
            ✓ Post unlimited jobs for 1 month
          </Typography>
          <Typography level="body-sm" sx={{ color: '#1A1B1D', display: 'flex', alignItems: 'center', gap: 1 }}>
            ✓ View all GEEK candidates
          </Typography>
          <Typography level="body-sm" sx={{ color: '#1A1B1D', display: 'flex', alignItems: 'center', gap: 1 }}>
            ✓ Schedule unlimited meetings
          </Typography>
        </Stack>
      </Box>

      {/* Promo Code Section */}
      <Box>
        <FormControl>
          <FormLabel sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
            Promo Code (Optional)
          </FormLabel>
            <Input
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => onPromoCodeChange(e.target.value)}
              onBlur={onPromoCodeBlur}
            sx={{
              backgroundColor: '#F8F9FA',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              '&:focus-within': {
                borderColor: '#2e2d7e',
              }
            }}
          />
          {promoCode === 'FIRSTPOSTFREE' && !hasExistingPositions && (
            <Alert color="success" variant="soft" sx={{ mt: 2 }}>
              🎉 Promo code applied! Your first job posting is free.
            </Alert>
          )}
          {promoCode === 'FIRSTPOSTFREE' && hasExistingPositions && (
            <Alert color="warning" variant="soft" sx={{ mt: 2 }}>
              This promo code is only valid for employers posting their first job.
            </Alert>
          )}
          {promoCode && promoCode !== 'FIRSTPOSTFREE' && (
            <Alert color="neutral" variant="soft" sx={{ mt: 2 }}>
              Invalid promo code.
            </Alert>
          )}
        </FormControl>
      </Box>

      {/* Only show payment section if no valid promo code */}
      {!isValidPromoCode && (
        <Box>
          <Typography level="h4" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 3 }}>
            Payment Information
          </Typography>
        
        <Box sx={{ 
          p: 3, 
          backgroundColor: '#F8F9FA', 
          borderRadius: '12px', 
          border: '1px solid #E5E7EB' 
        }}>
          <PaymentElement 
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card', 'apple_pay', 'google_pay']
            }}
          />
        </Box>
        </Box>
      )}

      {error && (
        <Alert color="danger" variant="soft">
          {error}
        </Alert>
      )}

      {/* Security Notice - only show when payment is required */}
      {!isValidPromoCode && (
        <Box sx={{ p: 3, backgroundColor: '#F0F8FF', borderRadius: '12px', border: '2px solid #E3F2FD' }}>
          <Typography level="body-sm" sx={{ color: '#7B7F85', textAlign: 'center' }}>
            🔒 Your payment information is secure and encrypted. We never store your card details.
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

const PaymentStep: React.FC<PaymentStepProps> = ({ data, onUpdate, onNext, isSubmitting, onFormReady }) => {
  const { sessionEmployerId } = useSession();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingPositions, setHasExistingPositions] = useState(false);
  const [promoCode, setPromoCode] = useState(data.promoCode || '');
  const [debouncedPromoCode, setDebouncedPromoCode] = useState(data.promoCode || '');

  // Initialize debounced promo code on mount
  useEffect(() => {
    setDebouncedPromoCode(promoCode);
  }, []); // Only run on mount

  const handleFormReady = React.useCallback((isReady: boolean) => {
    if (onFormReady) {
      onFormReady(isReady);
    }
  }, [onFormReady]);

  const handlePromoCodeChange = (code: string) => {
    // Remove spaces and convert to uppercase
    const cleanedCode = code.replace(/\s/g, '').toUpperCase();
    setPromoCode(cleanedCode);
    onUpdate({ promoCode: cleanedCode });
  };

  // Handle promo code blur for payment form refresh
  const handlePromoCodeBlur = () => {
    setDebouncedPromoCode(promoCode);
  };

  useEffect(() => {
    const initializePayment = async () => {
      if (!sessionEmployerId) {
        setError('No employer ID available. Please sign in again.');
        setIsLoading(false);
        return;
      }

      try {
        // First, check if employer has existing positions
        const positionsResponse = await getEmployerPositions(sessionEmployerId, { per_page: 1 }, true);
        const existingPositions = positionsResponse.data?.employerPositions || [];
        setHasExistingPositions(existingPositions.length > 0);

        // If promo code is valid, skip payment intent creation
        const isValidPromo = debouncedPromoCode === 'FIRSTPOSTFREE' && existingPositions.length === 0;
        if (isValidPromo) {
          console.log('Valid promo code detected, skipping payment intent creation');
          setIsLoading(false);
          return;
        }

        // Create payment intent for regular payment
        const response = await getSubscriptionPaymentIntent(sessionEmployerId);
        console.log('Payment intent response:', response);
        
        // Handle the actual API response structure
        const clientSecretValue = response.data?.clientSecret;
        const paymentIntentIdValue = response.data?.paymentIntentId;
        
        if (!clientSecretValue) {
          console.error('No client secret found in response:', response);
          setError('Payment initialization failed - no client secret received.');
          setIsLoading(false);
          return;
        }
        
        if (!paymentIntentIdValue) {
          console.error('No payment intent ID found in response:', response);
          setError('Payment initialization failed - no payment intent ID received.');
          setIsLoading(false);
          return;
        }
        
        console.log('Client secret received:', clientSecretValue);
        console.log('Payment intent ID received:', paymentIntentIdValue);
        setClientSecret(clientSecretValue);
        setPaymentIntentId(paymentIntentIdValue);
      } catch (err: any) {
        console.error('Error initializing payment:', err);
        console.error('Error details:', err.response?.data);
        setError(`Failed to initialize payment: ${err.response?.data?.message || err.message || 'Please try again.'}`);
      } finally {
        setIsLoading(false);
      }
    };

    initializePayment();
  }, [sessionEmployerId, debouncedPromoCode]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  // Check if we have a valid promo code (use debounced for payment logic)
  const isValidPromoCode = data.promoCode === 'FIRSTPOSTFREE' && !hasExistingPositions;

  if (error && !isValidPromoCode) {
    return (
      <Box sx={{ padding: '20px' }}>
        <Alert color="danger" variant="soft">
          {error || 'Unable to load payment form. Please try again.'}
        </Alert>
      </Box>
    );
  }

  // If we have a valid promo code, render the form without Stripe
  if (isValidPromoCode) {
    return (
      <Stack spacing={4}>
        {/* Plan Description */}
        <Box sx={{ 
          p: 3, 
          backgroundColor: '#F8F9FA', 
          borderRadius: '12px', 
          border: '2px solid #E5E7EB',
          textAlign: 'center'
        }}>
          <Typography level="h3" sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
            Employer Unlimited Plan
          </Typography>
          <Typography level="h2" sx={{ fontWeight: 700, color: '#2e2d7e', mb: 2 }}>
            FREE with promo code!
          </Typography>
          <Stack spacing={1} sx={{ textAlign: 'left', maxWidth: '400px', mx: 'auto' }}>
            <Typography level="body-sm" sx={{ color: '#1A1B1D', display: 'flex', alignItems: 'center', gap: 1 }}>
              ✓ Post unlimited jobs for 1 month
            </Typography>
            <Typography level="body-sm" sx={{ color: '#1A1B1D', display: 'flex', alignItems: 'center', gap: 1 }}>
              ✓ View all GEEK candidates
            </Typography>
            <Typography level="body-sm" sx={{ color: '#1A1B1D', display: 'flex', alignItems: 'center', gap: 1 }}>
              ✓ Schedule unlimited meetings
            </Typography>
          </Stack>
        </Box>

        {/* Promo Code Section */}
        <Box>
          <FormControl>
            <FormLabel sx={{ fontWeight: 600, color: '#1A1B1D', mb: 1 }}>
              Promo Code (Optional)
            </FormLabel>
            <Input
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => handlePromoCodeChange(e.target.value)}
              onBlur={handlePromoCodeBlur}
              sx={{
                backgroundColor: '#F8F9FA',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                '&:focus-within': {
                  borderColor: '#2e2d7e',
                }
              }}
            />
            <Alert color="success" variant="soft" sx={{ mt: 2 }}>
              🎉 Promo code applied! Your first job posting is free.
            </Alert>
          </FormControl>
        </Box>
      </Stack>
    );
  }

  // Regular payment flow - require client secret and payment intent
  if (!clientSecret || !paymentIntentId) {
    return (
      <Box sx={{ padding: '20px' }}>
        <Alert color="danger" variant="soft">
          {error || 'Unable to load payment form. Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Elements
      key={clientSecret} // Force re-render when clientSecret changes
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { 
          theme: 'stripe',
          variables: {
            colorPrimary: '#2e2d7e',
          }
        },
      }}
    >
      <PaymentForm 
        clientSecret={clientSecret} 
        paymentIntentId={paymentIntentId} 
        onNext={onNext}
        isSubmitting={isSubmitting}
        onFormReady={handleFormReady}
        promoCode={promoCode}
        onPromoCodeChange={handlePromoCodeChange}
        onPromoCodeBlur={handlePromoCodeBlur}
        hasExistingPositions={hasExistingPositions}
      />
    </Elements>
  );
};

export default PaymentStep;
